/**
 * Item-group membership is DERIVED from two product fields — never from `groupingName`:
 * a ROOT has `itemGroupName` and no `itemGroupRootId`; a CHILD has `itemGroupRootId`
 * (= the root's `appointmentProposalOptionProductId`). Anything else is a normal line.
 *
 * Structurally typed so the builder client, presentation client, and PDF lib can all
 * pass their own product shapes.
 */
export interface ItemGroupProductFields {
  appointmentProposalOptionProductId: string;
  appointmentProposalOptionId?: string;
  itemGroupName?: string | null;
  itemGroupRootId?: string | null;
  sortOrder?: number;
}

export interface ItemGroupPricingFields {
  price: number;
  quantity: number;
  hasMembership?: boolean;
  membershipPrice?: number | null;
}

export interface ItemGroup<T> {
  root: T;
  name: string;
  children: T[];
  total: number;
  membershipTotal: number;
  hasMembership: boolean;
}

export interface ItemGroupDerivation<T> {
  displayProducts: T[];
  itemGroupsByRootId: Map<string, ItemGroup<T>>;
}

export const isItemGroupChild = (
  product: Pick<ItemGroupProductFields, 'itemGroupRootId'>
): boolean => !!product.itemGroupRootId;

export const isItemGroupRoot = (product: ItemGroupProductFields): boolean =>
  !!product.itemGroupName && !product.itemGroupRootId;

export const getGroupChildren = <T extends ItemGroupProductFields>(
  products: T[],
  rootId: string
): T[] =>
  products
    .filter((product) => product.itemGroupRootId === rootId)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

const memberPrice = (product: ItemGroupPricingFields): number =>
  (product.hasMembership && product.membershipPrice != null
    ? product.membershipPrice
    : product.price) * product.quantity;

/**
 * Splits a flat product list into display rows and item groups.
 *
 * - A child is grouped (hidden from `displayProducts`) only when its root is present
 *   in the input, is itself a root (no nesting), and belongs to the same option when
 *   both sides carry an option id. Orphan children degrade to normal display rows.
 * - A root with zero present children is a plain row, not a group.
 * - Totals are display-only aggregates; option totals already sum every row.
 */
export const deriveItemGroups = <
  T extends ItemGroupProductFields & ItemGroupPricingFields,
>(
  products: T[]
): ItemGroupDerivation<T> => {
  const rootsById = new Map(
    products
      .filter(isItemGroupRoot)
      .map((product) => [product.appointmentProposalOptionProductId, product])
  );

  const resolveRoot = (child: T): T | undefined => {
    if (!child.itemGroupRootId) {
      return undefined;
    }
    const root = rootsById.get(child.itemGroupRootId);
    if (!root) {
      return undefined;
    }
    return !child.appointmentProposalOptionId ||
      !root.appointmentProposalOptionId ||
      child.appointmentProposalOptionId === root.appointmentProposalOptionId
      ? root
      : undefined;
  };

  const childrenByRootId = products.reduce((acc, product) => {
    const root = resolveRoot(product);
    if (!root) {
      return acc;
    }
    const rootId = root.appointmentProposalOptionProductId;
    return acc.set(rootId, [...(acc.get(rootId) ?? []), product]);
  }, new Map<string, T[]>());

  const itemGroupsByRootId = new Map(
    [...childrenByRootId.entries()].flatMap(([rootId, children]) => {
      const root = rootsById.get(rootId);
      if (!root) {
        return [];
      }
      const members = [root, ...children];
      const group: ItemGroup<T> = {
        root,
        name: root.itemGroupName || '',
        children,
        total: members.reduce(
          (sum, member) => sum + member.price * member.quantity,
          0
        ),
        membershipTotal: members.reduce(
          (sum, member) => sum + memberPrice(member),
          0
        ),
        hasMembership: members.some(
          (member) => !!member.hasMembership && member.membershipPrice != null
        ),
      };
      return [[rootId, group] as const];
    })
  );

  return {
    displayProducts: products.filter((product) => !resolveRoot(product)),
    itemGroupsByRootId,
  };
};

/**
 * Builder item count for an option: a group counts as ONE item (its root),
 * LINKED_ITEM rows are excluded, orphan children count as normal items.
 */
export const countOptionItems = <
  T extends ItemGroupProductFields &
    ItemGroupPricingFields & { groupingName: string },
>(
  products: T[]
): number =>
  deriveItemGroups(products).displayProducts.filter(
    (product) => product.groupingName !== 'LINKED_ITEM'
  ).length;
